import {Component, effect, inject, OnDestroy, OnInit, Signal, signal, ViewChild, ViewContainerRef} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {map, shareReplay} from 'rxjs/operators';
import {NewAppointmentComponent, NewAppointmentDialogData} from '../new-appointment/new-appointment.component';
import {AppointmentType, Appointment, CalendarAppointment, calendarAppointmentToWriteModel, describeRecurrence, Events, instantToWallClock, movesToAnotherDay, Patient, PatientFlagSetChangedPayload, QueryPatient, RECURRING_DATE_LOCKED_MESSAGE, SeriesScope, WebSocketService} from '@xmedical/core';
import {patchFlagsForPatient} from '../../utils/patch-flags-for-patient';
import {DayTimeSlot, DayViewComponent} from '../day-view/day-view.component';
import {MatDialog} from '@angular/material/dialog';
import {AppointmentDetailDialogComponent, AppointmentDetailDialogData} from '../appointment-detail-dialog/appointment-detail-dialog.component';
import {AppointmentMoveRequest} from '../fullcalendar-view/fullcalendar-view.component';
import {ConfirmationDialogComponent} from '@xmedical/ui';
import {EditAppointmentComponent} from '../edit-appointment/edit-appointment.component';
import {AppointmentScopeDialogComponent} from '../appointment-scope-dialog/appointment-scope-dialog.component';
import {FormArray, FormBuilder, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {CalendarStore} from '../../stores/calendar.store';
import {CALENDAR_ACTION_HANDLER, CALENDAR_DATA_PROVIDER, CALENDAR_HAS_TABS, CALENDAR_SELECTION_STORE, CalendarSelectionStore} from '../../tokens/calendar.tokens';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatButtonModule, MatFabButton} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatCardModule} from '@angular/material/card';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatMenuModule} from '@angular/material/menu';
import {AsyncPipe} from '@angular/common';
import {XmedicalUiModule} from '@xmedical/ui';
import {FullcalendarViewComponent} from '../fullcalendar-view/fullcalendar-view.component';
import dayjs from 'dayjs/esm';
import utc from 'dayjs/esm/plugin/utc';
import timezone from 'dayjs/esm/plugin/timezone';
import {formatZoneLabel, PracticeLocation, PracticePractitioner, PracticeTimezoneService, zonesDifferAt} from '@xmedical/core';

dayjs.extend(utc);
dayjs.extend(timezone);

type Dayjs = dayjs.Dayjs;

type NavigatorStep = 'day' | 'week' | 'month';

const START_HOUR = 7;
const END_HOUR = 18;
const TIMESLOT_MINS = 15;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  providers: [CalendarStore],
  imports: [
    ReactiveFormsModule,
    MatToolbarModule,
    MatButtonModule,
    MatFabButton,
    MatIconModule,
    MatSidenavModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatCardModule,
    MatButtonToggleModule,
    MatMenuModule,
    AsyncPipe,
    XmedicalUiModule,
    DayViewComponent,
    FullcalendarViewComponent,
  ]
})
export class CalendarComponent implements OnInit, OnDestroy {
  currentDate: Date;
  view: string;
  intervals: Date[] = [];
  visibleAppointmentTypes: number[] = [];
  appointmentTypes: AppointmentType[] = [];
  selectedAppointments: CalendarAppointment[] = [];
  selectedRow: DayTimeSlot = null;
  locations: PracticeLocation[] = [];
  practitioners: PracticePractitioner[] = [];
  selectedLocationId: number | null = null;
  selectedPractitionerId: number | null = null;
  locationsDisabled = false;
  practitionersDisabled = false;
  readonly initialized = signal(false);

  // Data for presentational DayView
  readonly dayViewAppointments = signal<CalendarAppointment[]>([]);

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );
  isExtraSmall = false;
  isExtraSmall$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.XSmall)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );
  calendarForm: UntypedFormGroup;

  get hasTabs(): boolean {
    return this.hasTabsSignal?.() ?? false;
  }

  get fixedTopGap(): number {
    if (this.hasTabs && this.isExtraSmall) {
      return 160;
    }
    if (this.hasTabs && !this.isExtraSmall) {
      return 172;
    }
    if (!this.hasTabs && this.isExtraSmall) {
      return 112;
    }
    if (!this.hasTabs && !this.isExtraSmall) {
      return 128;
    }
    return 0;
  }

  private formEventsSubscription: Subscription;
  private routeParamsSubscription: Subscription;
  private handsetSizeSubscription: Subscription;
  private dayViewSubscription: Subscription;

  // WebSocket management (centralized for both views)
  private subscribedChannelNames: string[] = [];
  private wsCallback: ((data: unknown) => void) | null = null;
  private flagsCallback: ((data: PatientFlagSetChangedPayload) => void) | null = null;

  // Cached FullCalendar date range for filter reloads
  private fullCalendarStartDate = '';
  private fullCalendarEndDate = '';

  @ViewChild(DayViewComponent) dayView: DayViewComponent;
  @ViewChild(FullcalendarViewComponent) fullCalendarView: FullcalendarViewComponent;

  private dataProvider = inject(CALENDAR_DATA_PROVIDER);
  private actionHandler = inject(CALENDAR_ACTION_HANDLER);
  private practiceTz = inject(PracticeTimezoneService);
  readonly calendarStore = inject(CalendarStore);
  private pushService = inject(WebSocketService);
  private vcr = inject(ViewContainerRef);
  private snackBar = inject(MatSnackBar);
  private hasTabsSignal: Signal<boolean> | null = inject(CALENDAR_HAS_TABS, {optional: true});
  private selectionStore: CalendarSelectionStore | null = inject(CALENDAR_SELECTION_STORE, {optional: true});

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
  ) {
    effect(() => {
      if (this.dataProvider.isReady() && !this.initialized()) {
        this.initializeData();
      }
    });
  }

  get appointmentTypesFormArray(): FormArray {
    return this.calendarForm?.get('appointmentTypes') as FormArray ?? new FormArray([]);
  }

  get loaded(): boolean {
    return this.dataProvider.isReady() && !!this.calendarForm;
  }

  get navigatorStep(): NavigatorStep {
    return this.view === 'week' || this.view === 'month' ? this.view : 'day';
  }

  get patientDisplayFormat() {
    return this.dataProvider.practiceSettings()?.defaultPatientDisplayFormat ?? null;
  }

  get allowDragCreate(): boolean {
    return this.dataProvider.userSettings?.()?.allowDragCreate ?? true;
  }

  get allowTapCreate(): boolean {
    return this.dataProvider.userSettings?.()?.allowTapCreate ?? true;
  }

  get allowDragEditing(): boolean {
    return this.dataProvider.userSettings?.()?.allowDragEditing ?? false;
  }

  get allowFabCreate(): boolean {
    return this.dataProvider.userSettings?.()?.allowFabCreate ?? false;
  }

  get viewLabel(): string {
    switch (this.view) {
      case 'daysheet': return 'Daysheet';
      case 'day': return 'Day';
      case 'week': return 'Week';
      case 'month': return 'Month';
      default: return '';
    }
  }

  get appointmentsSelected(): boolean {
    return this.selectedAppointments.length > 0;
  }

  /** Label for the persistent "Times shown in …" chip, e.g. "SAST (GMT+2)". */
  get practiceTimezoneLabel(): string {
    return formatZoneLabel(this.practiceTz.practiceTimezone());
  }

  /** The viewer's own zone label, shown in the mismatch banner. */
  get deviceTimezoneLabel(): string {
    return formatZoneLabel(this.practiceTz.deviceTimezone);
  }

  /** True when the viewer's device zone resolves to a different offset than the practice zone. */
  get viewerOutOfPracticeZone(): boolean {
    return zonesDifferAt(new Date(), this.practiceTz.practiceTimezone(), this.practiceTz.deviceTimezone);
  }

  ngOnInit(): void {
    this.routeParamsSubscription = this.activatedRoute.paramMap.subscribe(params => {
      const view = params.get('view');
      const year = parseInt(params.get('year'), 10);
      const monthIndex = parseInt(params.get('month'), 10) - 1;
      const day = parseInt(params.get('day'), 10);
      this.currentDate = new Date(year, monthIndex, day);
      this.view = view;
      this.setupIntervals();
      if (this.initialized() && this.view === 'daysheet') {
        this.loadDayViewAppointments();
      }
    });
    this.handsetSizeSubscription = this.isExtraSmall$.subscribe(result => this.isExtraSmall = result);
  }

  private initializeData(): void {
    this.initialized.set(true);

    this.appointmentTypes = this.dataProvider.appointmentTypes().sort((a, b) => a.id > b.id ? 1 : -1) ?? [];
    this.visibleAppointmentTypes = this.dataProvider.defaultVisibleAppointmentTypeIds();

    this.locations = this.dataProvider.practiceLocations().filter(l => !l.isDeleted);
    this.locationsDisabled = this.locations.length <= 1;
    const defaultLocation = this.locations.find(l => l.isDefault) ?? this.locations[0];
    this.selectedLocationId = defaultLocation?.id ?? null;

    this.practitioners = this.dataProvider.practicePractitioners().filter(p => !p.isDeleted);
    this.practitionersDisabled = this.practitioners.length <= 1;
    const linkedId = this.dataProvider.linkedPracticePractitionerId?.();
    const linkedPractitioner = linkedId ? this.practitioners.find(p => p.id === linkedId) : null;
    const primaryPractitioner = linkedPractitioner ?? this.practitioners.find(p => p.isPrimary) ?? this.practitioners[0];
    this.selectedPractitionerId = primaryPractitioner?.id ?? null;

    this.restorePersistedSelection();

    this.calendarForm = this.formBuilder.group({
      appointmentTypes: this.formBuilder.array([])
    });
    this.appointmentTypes.forEach(type => {
      const isSelected = this.dataProvider.defaultVisibleAppointmentTypeIds().includes(type.id);
      const control = this.formBuilder.group({
        id: [type.id],
        selected: [isSelected]
      });
      this.appointmentTypesFormArray.push(control);
    });
    // Filtering is client-side (no server reload), so react immediately to each
    // checkbox toggle rather than debouncing — a delay reads as "not reacting".
    this.formEventsSubscription = this.calendarForm.valueChanges.subscribe(value => {
      this.visibleAppointmentTypes = value.appointmentTypes.filter((type: { id: number, selected: boolean }) => type.selected).map((type: { id: number, selected: boolean }) => type.id);
    });

    this.setupWebSocket();

    if (this.currentDate && this.view === 'daysheet') {
      this.loadDayViewAppointments();
    } else if (this.fullCalendarStartDate && this.fullCalendarEndDate) {
      this.calendarStore.loadAppointments(
        this.fullCalendarStartDate,
        this.fullCalendarEndDate,
        this.selectedLocationId,
        this.selectedPractitionerId
      );
    }
  }

  ngOnDestroy(): void {
    this.routeParamsSubscription?.unsubscribe();
    this.handsetSizeSubscription?.unsubscribe();
    this.formEventsSubscription?.unsubscribe();
    this.dayViewSubscription?.unsubscribe();
    this.teardownWebSocket();
  }

  onAppointmentSelectionChanged(selectedAppointments: CalendarAppointment[]): void {
    this.selectedAppointments = selectedAppointments;
  }

  onRowSelectionChanged(selectedRow: DayTimeSlot): void {
    this.selectedRow = selectedRow;
  }

  dateChanged(date: Dayjs | Date) {
    const d = dayjs.isDayjs(date) ? date : dayjs(date);
    if (!d.isValid()) {
      return;
    }
    const month = d.month() + 1;
    const day = d.date();
    const year = d.year();
    const url = this.actionHandler.buildCalendarRoute(this.view, year, month, day);
    this.router.navigateByUrl(url);
  }

  selectToday() {
    this.dateChanged(dayjs());
  }

  sendBulkMessage() {
    this.actionHandler.onSendBulkMessageRequested?.(this.selectedAppointments.map(a => a.patient));
  }

  sendBulkEmail() {
    this.actionHandler.onSendBulkEmailRequested?.(this.selectedAppointments.map(a => a.patient));
  }

  addAppointmentFromFab() {
    const now = new Date();
    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const end = new Date(now.getTime() + defaultDuration * 60 * 1000);
    this.onHelperEventClicked({start: now, end});
  }

  /** True when an appointment-type filter is narrowing the grid, so some appointments
   *  may be hidden. Forwarded into the booking dialogs' clash banner so it doesn't
   *  suppress a same-location clash the user can't currently see. Mirrors the grid's
   *  own filter predicate (it only filters when the visible-types list is non-empty). */
  get appointmentTypeFiltersActive(): boolean {
    return this.visibleAppointmentTypes.length > 0;
  }

  addNewAppointment() {
    const selectedTimeslot = this.selectedRow.timeslot;
    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const endDate = new Date(selectedTimeslot.getTime() + defaultDuration * 60 * 1000);
    const dialogRef = this.dialog.open(NewAppointmentComponent, {
      disableClose: true,
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        selectedDate: selectedTimeslot,
        selectedEndDate: endDate,
        intervals: this.intervals,
        practiceLocationId: this.selectedLocationId,
        practicePractitionerId: this.selectedPractitionerId,
        appointmentTypeFiltersActive: this.appointmentTypeFiltersActive
      } as NewAppointmentDialogData
    });
    dialogRef.afterClosed().subscribe(() => {
      if (this.selectedRow !== null) {
        this.dayView.selectRow(this.selectedRow);
      }
      this.loadDayViewAppointments();
    });
  }

  onViewChanged(newView: string): void {
    const d = dayjs(this.currentDate);
    const month = d.month() + 1;
    const day = d.date();
    const year = d.year();
    const url = this.actionHandler.buildCalendarRoute(newView, year, month, day);
    this.router.navigateByUrl(url);
  }

  onAppointmentMoved(request: AppointmentMoveRequest): void {
    const zone = this.practiceTz.practiceTimezone();

    // A recurring occurrence may move its time of day but not its date. Refuse the
    // cross-day drag here rather than letting the server do it: the failure path only
    // reverts, so a server rejection would snap the appointment back with no explanation.
    if (request.appointment.recurringId && movesToAnotherDay(request.appointment.start, request.start, zone)) {
      request.revert();
      this.snackBar.open(RECURRING_DATE_LOCKED_MESSAGE, 'Dismiss', {duration: 8000});
      return;
    }

    // For a series occurrence the scope dialog is itself the confirmation — same as the
    // cancel path — so it replaces the move prompt rather than following it.
    if (request.appointment.recurringId) {
      const scopeRef = this.dialog.open(AppointmentScopeDialogComponent, {
        viewContainerRef: this.vcr,
        data: {action: 'edit', summary: describeRecurrence(request.appointment.recurrenceRule)},
      });
      scopeRef.afterClosed().subscribe((scope: SeriesScope | undefined) => {
        if (scope) {
          this.applyAppointmentMove(request, scope);
        } else {
          request.revert();
        }
      });
      return;
    }

    const title = request.appointment.patient?.formalName
      || request.appointment.patient?.name
      || request.appointment.title
      || 'appointment';
    // request.start is a true instant (FullCalendar is anchored to the practice
    // zone); render the confirmation in practice wall-clock.
    const formattedStart = dayjs(request.start).tz(zone).format('ddd, D MMM HH:mm');
    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Move Appointment',
        message: `Move ${title} to ${formattedStart}?`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        request.revert();
        return;
      }
      this.applyAppointmentMove(request, undefined);
    });
  }

  private applyAppointmentMove(request: AppointmentMoveRequest, scope: SeriesScope | undefined): void {
    // request.start/end are practice-zone instants from FullCalendar; send them
    // as zone-less wall-clock to match the appointment storage model.
    const zone = this.practiceTz.practiceTimezone();
    const updated = calendarAppointmentToWriteModel(request.appointment, {
      start: instantToWallClock(request.start, zone),
      end: request.end ? instantToWallClock(request.end, zone) : undefined,
      scope,
    });
    this.dataProvider.updateAppointment(updated).subscribe({
      next: () => this.calendarStore.refreshAppointments(),
      error: () => {
        request.revert();
        this.calendarStore.refreshAppointments();
      },
    });
  }

  onFullCalendarAppointmentClicked(appointment: CalendarAppointment): void {
    const dialogRef = this.dialog.open(AppointmentDetailDialogComponent, {
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        appointment,
        appointmentTypeFiltersActive: this.appointmentTypeFiltersActive
      } as AppointmentDetailDialogData
    });
    dialogRef.afterClosed().subscribe(() => {
      this.calendarStore.refreshAppointments();
    });
  }

  onHelperEventClicked(event: { start: Date; end: Date }): void {
    if (!this.appointmentTypes?.length) {
      this.fullCalendarView?.removeHelperEvent();
      return;
    }
    const dialogRef = this.dialog.open(NewAppointmentComponent, {
      disableClose: true,
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        selectedDate: event.start,
        selectedEndDate: event.end,
        intervals: this.intervals,
        practiceLocationId: this.selectedLocationId,
        practicePractitionerId: this.selectedPractitionerId,
        appointmentTypeFiltersActive: this.appointmentTypeFiltersActive
      } as NewAppointmentDialogData
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fullCalendarView?.removeHelperEvent();
      this.calendarStore.refreshAppointments();
    });
  }

  onFilterChanged(): void {
    this.selectionStore?.save({
      locationId: this.selectedLocationId,
      practitionerId: this.selectedPractitionerId,
    });
    if (this.view === 'daysheet') {
      this.loadDayViewAppointments();
    } else if (this.fullCalendarStartDate && this.fullCalendarEndDate) {
      this.calendarStore.loadAppointments(
        this.fullCalendarStartDate,
        this.fullCalendarEndDate,
        this.selectedLocationId,
        this.selectedPractitionerId
      );
    }
  }

  /**
   * Override the computed default location/practitioner with the user's
   * persisted selection, when present. Each id is only applied if it still
   * exists in the loaded lists (a deleted location or removed practitioner
   * falls back to the default). A persisted null means an explicit "All".
   */
  private restorePersistedSelection(): void {
    const persisted = this.selectionStore?.load();
    if (!persisted) {
      return;
    }
    if (persisted.locationId === null || this.locations.some(l => l.id === persisted.locationId)) {
      this.selectedLocationId = persisted.locationId;
    }
    if (persisted.practitionerId === null || this.practitioners.some(p => p.id === persisted.practitionerId)) {
      this.selectedPractitionerId = persisted.practitionerId;
    }
  }

  // --- DayView output handlers ---

  onDayViewDeleteRequested(appointment: CalendarAppointment): void {
    // A series occurrence must say how far the cancel reaches before anything is deleted.
    if (appointment.recurringId) {
      const scopeRef = this.dialog.open(AppointmentScopeDialogComponent, {
        viewContainerRef: this.vcr,
        data: {action: 'cancel', summary: describeRecurrence(appointment.recurrenceRule)},
      });
      scopeRef.afterClosed().subscribe((scope: SeriesScope | undefined) => {
        if (scope) {
          this.deleteDayViewAppointment(appointment.id, scope);
        }
      });
      return;
    }
    this.deleteDayViewAppointment(appointment.id, undefined);
  }

  private deleteDayViewAppointment(appointmentId: number, scope: SeriesScope | undefined): void {
    this.dataProvider.deleteAppointment(appointmentId, scope).subscribe(() => {
      this.loadDayViewAppointments();
    });
  }

  onDayViewEditRequested(appointment: CalendarAppointment): void {
    const dialogRef = this.dialog.open(EditAppointmentComponent, {
      disableClose: true,
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        appointment,
        appointmentTypeFiltersActive: this.appointmentTypeFiltersActive
      }
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadDayViewAppointments();
    });
  }

  onDayViewAddRequested(event: { timeslot: Date }): void {
    const defaultDuration = this.dataProvider.practiceSettings()?.defaultAppointmentDurationMinutes ?? 30;
    const endDate = new Date(event.timeslot.getTime() + defaultDuration * 60 * 1000);
    const dialogRef = this.dialog.open(NewAppointmentComponent, {
      disableClose: true,
      viewContainerRef: this.vcr,
      panelClass: 'mat-confirmation-dialog-responsive',
      data: {
        appointmentTypes: this.appointmentTypes.sort((a, b) => a.id > b.id ? 1 : -1),
        selectedDate: event.timeslot,
        selectedEndDate: endDate,
        intervals: this.intervals,
        practiceLocationId: this.selectedLocationId,
        practicePractitionerId: this.selectedPractitionerId,
        appointmentTypeFiltersActive: this.appointmentTypeFiltersActive
      } as NewAppointmentDialogData
    });
    dialogRef.afterClosed().subscribe(() => {
      if (this.selectedRow !== null) {
        this.dayView?.selectRow(this.selectedRow);
      }
      this.loadDayViewAppointments();
    });
  }

  onDayViewViewPatient(patient: Patient | QueryPatient): void {
    this.actionHandler.onViewPatientRequested(patient);
  }

  onDayViewCreatePatient(appointment: CalendarAppointment): void {
    this.actionHandler.onCreatePatientRequested(appointment).subscribe(patient => {
      if (patient) {
        const updated = calendarAppointmentToWriteModel(appointment, {patientId: patient.id});
        this.dataProvider.updateAppointment(updated).subscribe(() => {
          this.loadDayViewAppointments();
        });
      }
    });
  }

  onDayViewSendMessage(patient: Patient | QueryPatient): void {
    this.actionHandler.onSendMessageRequested?.(patient);
  }

  onDayViewSendEmail(patient: Patient | QueryPatient): void {
    this.actionHandler.onSendEmailRequested?.(patient);
  }

  // --- FullcalendarView output handler ---

  onDateRangeChanged(event: { startDate: string; endDate: string }): void {
    this.fullCalendarStartDate = event.startDate;
    this.fullCalendarEndDate = event.endDate;
    if (!this.initialized()) {
      return;
    }
    this.calendarStore.loadAppointments(
      event.startDate,
      event.endDate,
      this.selectedLocationId,
      this.selectedPractitionerId
    );
  }

  // --- Data loading for DayView ---

  loadDayViewAppointments(): void {
    if (!this.currentDate || !this.intervals?.length) {
      return;
    }
    this.dayViewSubscription?.unsubscribe();
    this.dayViewSubscription = this.dataProvider.loadAppointmentsByDay(
      this.currentDate, this.selectedLocationId, this.selectedPractitionerId
    ).subscribe(appointments => {
      this.dayViewAppointments.set(appointments);
    });
  }

  // --- WebSocket management ---

  private setupWebSocket(): void {
    const channelNames = this.dataProvider.getEventChannelNames();
    this.wsCallback = () => this.onWebSocketEvent();
    this.flagsCallback = (data: PatientFlagSetChangedPayload) => this.onPatientFlagsChanged(data);
    channelNames.forEach(channelName => {
      this.pushService.subscribe(channelName, Events.AppointmentAppointmentChanged, this.wsCallback);
      this.pushService.subscribe(channelName, Events.AppointmentAppointmentFlagsChanged, this.flagsCallback);
    });
    this.subscribedChannelNames = channelNames;
  }

  private teardownWebSocket(): void {
    if (this.wsCallback || this.flagsCallback) {
      this.subscribedChannelNames.forEach(channelName => {
        if (this.wsCallback) {
          this.pushService.unsubscribe(channelName, Events.AppointmentAppointmentChanged, this.wsCallback);
        }
        if (this.flagsCallback) {
          this.pushService.unsubscribe(channelName, Events.AppointmentAppointmentFlagsChanged, this.flagsCallback);
        }
      });
      this.wsCallback = null;
      this.flagsCallback = null;
      this.subscribedChannelNames = [];
    }
  }

  private onWebSocketEvent(): void {
    if (this.view === 'daysheet') {
      this.loadDayViewAppointments();
    } else {
      this.calendarStore.refreshAppointments();
    }
  }

  private onPatientFlagsChanged(data: PatientFlagSetChangedPayload): void {
    if (!data || typeof data.patientId !== 'number' || !Array.isArray(data.flagIds)) {
      // Unexpected payload shape — reload as a safety net.
      this.onWebSocketEvent();
      return;
    }
    const flags = this.dataProvider.patientFlags?.();
    if (!flags) {
      // No flag metadata source available — fall back to a full reload.
      this.onWebSocketEvent();
      return;
    }
    const lookup = new Map(flags.map(f => [f.id, f] as const));
    this.calendarStore.patchPatientFlags(data.patientId, data.flagIds, lookup);
    const currentDayView = this.dayViewAppointments();
    const nextDayView = patchFlagsForPatient(currentDayView, data.patientId, data.flagIds, lookup);
    if (nextDayView !== currentDayView) {
      this.dayViewAppointments.set(nextDayView);
    }
  }

  private setupIntervals() {
    // Build the 07:00–18:00 slots as instants anchored to the practice zone, so
    // the daysheet shows and matches practice wall-clock regardless of the
    // viewer's device zone. For an in-zone viewer this equals the old
    // device-local slots — no change.
    const zone = this.practiceTz.practiceTimezone();
    const day = dayjs(this.currentDate).format('YYYY-MM-DD');
    const endSlot = dayjs.tz(`${day}T${pad(END_HOUR)}:00:00`, zone);
    let slot = dayjs.tz(`${day}T${pad(START_HOUR)}:00:00`, zone);
    const intervals: Date[] = [];
    while (!slot.isAfter(endSlot)) {
      intervals.push(slot.toDate());
      slot = slot.add(TIMESLOT_MINS, 'minute');
    }
    this.intervals = intervals;
  }
}
